import { createIntl } from '@edx/frontend-platform/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { StripeCheckoutStatus } from '@stripe/stripe-js';

import { paymentProcessedCheckoutIntentStates } from '@/components/app/data/services/context';
import {
  AccountDetailsSchema,
  BillingDetailsSchema,
  CheckoutPageRoute,
  DataStoreKey,
  EssentialsPageRoute,
  PlanDetailsSchema,
} from '@/constants/checkout';
import { checkoutFormStore } from '@/hooks/useCheckoutFormStore';
import {
  resolveEssentialsProduct,
} from '@/utils/resolveEssentialsProduct';

import type { SspProduct } from '@/components/app/data/services/ssp-products';

/**
 * Parameters for populateInitialApplicationState.
 */
type PopulateCompletedFormFieldsProps = {
  /** The checkout intent from the backend context, if any. */
  checkoutIntent: CheckoutContextCheckoutIntent | null,
  /** Stripe price id from the backend context */
  stripePriceId: CheckoutContextPrice['id'] | null,
  /** Resolved ssp_product_slug (always present; e.g. 'teams_yearly' or 'ai-academy-yearly') */
  sspProductSlug: string,
  /** The currently authenticated user as exposed by the platform auth util. */
  authenticatedUser: AuthenticatedUser,
};

/**
 * Summary of checkout intent state used by loaders to decide routing.
 */
interface DetermineExistingPaidCheckoutIntent {
  /**
   * Whether there is an existing successful checkout intent (paid or fulfilled).
   */
  existingSuccessfulCheckoutIntent: boolean;
  /**
   * Whether the checkout intent is expired with respect to current time.
   */
  expiredCheckoutIntent: boolean;
}

// For Essentials flow check
export const isEssentialsFlow = (): boolean => sessionStorage.getItem('isEssentials') === 'true';

/**
 * Computes a compact state object from an optional checkout intent.
 *
 * @param {ExtendedCheckoutContextCheckoutIntent | null} checkoutIntent - The checkout intent from the checkout context.
 * @returns {DetermineExistingPaidCheckoutIntent}
 *   Object indicating if a successful intent exists and if the intent is expired.
 */
const determineExistingCheckoutIntentState = (
  checkoutIntent: ExtendedCheckoutContextCheckoutIntent | null,
): DetermineExistingPaidCheckoutIntent => {
  if (!checkoutIntent) {
    return {
      existingSuccessfulCheckoutIntent: false,
      expiredCheckoutIntent: true,
    };
  }

  return {
    existingSuccessfulCheckoutIntent: checkoutIntent.existingSuccessfulCheckoutIntent!,
    expiredCheckoutIntent: checkoutIntent.expiredCheckoutIntent!,
  };
};

const mapCheckoutIntentStateToSessionStatus = (checkoutIntentState?: CheckoutIntentState): {
  type: StripeCheckoutStatus['type'] | null;
  paymentStatus: Extract<StripeCheckoutStatus, { type: 'complete' }>['paymentStatus'] | null
} => {
  if (!checkoutIntentState) {
    return {
      type: null,
      paymentStatus: null,
    };
  }
  if (paymentProcessedCheckoutIntentStates.includes(checkoutIntentState)) {
    return {
      type: 'complete',
      paymentStatus: 'paid',
    };
  }
  return {
    type: 'open',
    paymentStatus: null,
  };
};

/**
 * Populates relevant steps in the local checkout form store using information
 * from the authenticated user and (optionally) an existing checkout intent.
 *
 * This function intentionally performs a shallow merge into the current form state
 * so that any user-entered values are preserved unless explicitly overwritten.
 *
 * It sets:
 * - Plan Details: authenticated flag, full name, admin email, and country
 * - Account Details: enterprise slug and company name from the checkout intent
 *
 * @param {PopulateCompletedFormFieldsProps} params - Function parameters.
 * @returns {void}
 */
const populateInitialApplicationState = ({
  checkoutIntent,
  stripePriceId,
  sspProductSlug,
  authenticatedUser,
}: PopulateCompletedFormFieldsProps): void => {
  checkoutFormStore.setState(
    (s) => ({
      ...s,
      formData: {
        ...s.formData,
        [DataStoreKey.PlanDetails]: {
          ...s.formData[DataStoreKey.PlanDetails],
          quantity: s.formData[DataStoreKey.PlanDetails]?.quantity
            ?? checkoutIntent?.quantity
            ?? null,
          fullName: s.formData[DataStoreKey.PlanDetails]?.fullName
            ?? authenticatedUser?.name
            ?? authenticatedUser?.username,
          adminEmail: s.formData[DataStoreKey.PlanDetails]?.adminEmail
            ?? authenticatedUser?.email,
          country: s.formData[DataStoreKey.PlanDetails]?.country
            ?? authenticatedUser?.country
            ?? null,
          stripePriceId: s.formData[DataStoreKey.PlanDetails]?.stripePriceId
            ?? stripePriceId
            ?? null,
        },
        [DataStoreKey.AccountDetails]: {
          ...s.formData[DataStoreKey.AccountDetails],
          enterpriseSlug: s.formData[DataStoreKey.AccountDetails]?.enterpriseSlug
            ?? checkoutIntent?.enterpriseSlug,
          companyName: s.formData[DataStoreKey.AccountDetails]?.companyName
            ?? checkoutIntent?.enterpriseName,
        },
        [DataStoreKey.BillingDetails]: {
          ...s.formData[DataStoreKey.BillingDetails],
          confirmTnC: s.formData[DataStoreKey.BillingDetails]?.confirmTnC ?? false,
          confirmSubscription: s.formData[DataStoreKey.BillingDetails]?.confirmSubscription ?? false,
          confirmRecurringSubscription: s.formData[DataStoreKey.BillingDetails]?.confirmRecurringSubscription ?? false,
        },
      },
      checkoutSessionStatus: {
        ...s.checkoutSessionStatus,
        type: s.checkoutSessionStatus.type || mapCheckoutIntentStateToSessionStatus(checkoutIntent?.state).type,
        paymentStatus: s.checkoutSessionStatus.paymentStatus || mapCheckoutIntentStateToSessionStatus(
          checkoutIntent?.state,
        ).paymentStatus,
      },
      checkoutSessionClientSecret: checkoutIntent?.checkoutSessionClientSecret
        ?? s.checkoutSessionClientSecret,
    }),
    false,
  );

  // Ensure the ssp product slug is stored in the store (always present).
  checkoutFormStore.getState().setSspProductSlug(sspProductSlug);
};

/**
 * A union of all route path values defined on CheckoutPageRoute (e.g., "/plan-details").
 */
type CheckoutPageRouteValue = (typeof CheckoutPageRoute)[keyof typeof CheckoutPageRoute];

/**
 * Result of validating whether navigation to a given route is allowed based on form state.
 */
type ValidationResult = {
  /** Whether all prerequisites passed. */
  valid: boolean;
  /** If invalid, the first route the user should be redirected to fix validation. */
  invalidRoute?: CheckoutPageRouteValue | `/essentials${CheckoutPageRouteValue}`; // route of the first page that failed validation
};

/**
 * Build React Hook Form resolvers (zod-based) for the various checkout form slices,
 * using the provided field constraints and Stripe price ID where required.
 *
 * Note: Billing Details resolver can be added later when the schema is available.
 *
 * @param {CheckoutContextFieldConstraints} constraints - Field-level constraints from the BFF context.
 * @param {CheckoutContextPrice['id']} stripePriceId - Stripe price identifier used by the Plan Details schema.
 * @returns {{ planDetailsResolver: Function, accountDetailsResolver: Function }} An object with resolvers.
 */
const makeResolvers = (
  constraints: CheckoutContextFieldConstraints,
  stripePriceId: CheckoutContextPrice['id'],
  adminEmail: string | undefined = undefined,
) => {
  const intl = createIntl({ locale: 'en', messages: {} });

  const planDetailsResolver = zodResolver(PlanDetailsSchema(constraints, { stripePriceId, intl }));

  const accountDetailsResolver = zodResolver(AccountDetailsSchema(constraints, { adminEmail, intl }));

  const billingDetailsResolver = zodResolver(BillingDetailsSchema(constraints, { intl }));

  return {
    planDetailsResolver,
    accountDetailsResolver,
    billingDetailsResolver,
  };
};

interface PrerequisiteCheck<T> {
  pick: (formData: any) => T;
  getResolver: (
    constraints: CheckoutContextFieldConstraints,
    stripePriceId: CheckoutContextPrice['id'],
    adminEmail?: string,
  ) => (values: any, ctx?: any, opts?: any) => any;
  failRoute: CheckoutPageRouteValue | `/essentials${CheckoutPageRouteValue}`;
}

/**
 * For each target route, list the prerequisite slices to validate, in order.
 * Each entry includes which form slice to validate, how to build its resolver,
 * and the route that should be returned if that slice is invalid.
 */
export const prerequisiteSpec: Record<CheckoutStep, Array<PrerequisiteCheck<any>>> = {
  PlanDetails: [],
  AccountDetails: [
    {
      pick: (formData) => formData[DataStoreKey.PlanDetails] as PlanDetailsData,
      getResolver: (constraints, stripePriceId) => makeResolvers(constraints, stripePriceId).planDetailsResolver,
      failRoute: isEssentialsFlow() ? EssentialsPageRoute.PlanDetails : CheckoutPageRoute.PlanDetails,
    },
  ],
  BillingDetails: [
    {
      pick: (formData) => formData[DataStoreKey.PlanDetails] as PlanDetailsData,
      getResolver: (constraints, stripePriceId) => makeResolvers(constraints, stripePriceId).planDetailsResolver,
      failRoute: isEssentialsFlow() ? EssentialsPageRoute.PlanDetails : CheckoutPageRoute.PlanDetails,
    },
    {
      pick: (formData) => formData[DataStoreKey.AccountDetails] as AccountDetailsData,
      getResolver: (constraints, stripePriceId, adminEmail) => (
        makeResolvers(constraints, stripePriceId, adminEmail).accountDetailsResolver
      ),
      failRoute: isEssentialsFlow() ? EssentialsPageRoute.AccountDetails : CheckoutPageRoute.AccountDetails,
    },
  ],
};

/**
 * Validate all prerequisites for navigating to a target route.
 *
 * It evaluates, in order, the prerequisite form slices defined in `prerequisiteSpec` for the
 * provided `currentRoute`. Each slice is validated using its zod resolver (built from
 * backend-provided `constraints` and `stripePriceId` where needed). The first failing slice
 * determines the `invalidRoute` to redirect to.
 *
 * @param {Object} params - Parameters object.
 * @param {CheckoutStep} params.checkoutStep - Route path the user is attempting to visit.
 * @param {CheckoutContextFieldConstraints | null} params.constraints - Field constraints from BFF;
 *   if null, validation fails to Plan Details.
 * @param {CheckoutContextPrice['id']} params.stripePriceId - Stripe price id required by Plan Details resolver.
 * @returns {Promise<ValidationResult>} Validation result indicating whether navigation can proceed.
 */
const validateFormState = async ({
  checkoutStep,
  constraints,
  stripePriceId,
}: {
  checkoutStep: CheckoutStep;
  constraints: CheckoutContextFieldConstraints | null;
  stripePriceId: CheckoutContextPrice['id'];
}): Promise<ValidationResult> => {
  if (!checkoutStep) {
    return { valid: false,
      invalidRoute: isEssentialsFlow() ? EssentialsPageRoute.PlanDetails : CheckoutPageRoute.PlanDetails };
  }

  const { formData } = checkoutFormStore.getState();
  if (!constraints) {
    return { valid: false,
      invalidRoute: isEssentialsFlow() ? EssentialsPageRoute.PlanDetails : CheckoutPageRoute.PlanDetails };
  }

  const checks = prerequisiteSpec[checkoutStep] ?? [];

  // Extract adminEmail from Plan Details for use in validation
  const adminEmail = (formData[DataStoreKey.PlanDetails] as PlanDetailsData)?.adminEmail;

  // Build all validation promises up front, check against the defined zod resolver
  const validationPromises = checks.map(async ({ pick, getResolver, failRoute }) => {
    const values = pick(formData);
    const resolver = getResolver(constraints, stripePriceId, adminEmail);
    const { errors } = await resolver(values, undefined, { criteriaMode: 'all' });
    return { failRoute, hasErrors: errors && Object.keys(errors).length > 0 };
  });

  const results = await Promise.all(validationPromises);

  // Find the first failing validation (preserving order)
  const firstFail = results.find(result => result.hasErrors);
  if (firstFail) {
    return { valid: false, invalidRoute: firstFail.failRoute };
  }

  return { valid: true };
};

const hydrateEssentialsProduct = (
  allSspProducts: SspProduct[],
  productKey: string | null,
): void => {
  const resolvedProductKey = productKey || '';
  const selectedProduct = resolveEssentialsProduct(
    allSspProducts,
    resolvedProductKey,
  );
  if (selectedProduct) {
    checkoutFormStore.getState().setFormData(
      DataStoreKey.AcademySelection,
      { selectedProduct },
    );
    if (selectedProduct.lookupKey) {
      checkoutFormStore.getState().setProductLookupKey(selectedProduct.lookupKey);
    }
  }
};

export {
  determineExistingCheckoutIntentState,
  mapCheckoutIntentStateToSessionStatus,
  populateInitialApplicationState,
  validateFormState,
  hydrateEssentialsProduct,
};
